import { CreditCard } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { SubscriptionDetails } from "@/lib/api/billing";
import { useRegionalFormat } from "@/lib/regional-format/context";

type PaymentInfoProps = {
  subscription: SubscriptionDetails;
  t: {
    paymentMethod: string;
    noPaymentMethod: string;
    expires: string;
    active: string;
    pending: string;
    cancelled: string;
    pastDue: string;
    noSubscription: string;
    cancelsOn: string;
  };
};

export function PaymentInfo({ subscription, t }: PaymentInfoProps) {
  const { formatDate } = useRegionalFormat();
  const hasPaymentMethod = subscription.paymentMethod || subscription.cardLast4;

  const statusBadge = () => {
    switch (subscription.status) {
      case "active":
        return <Badge variant="default">{t.active}</Badge>;
      case "pending":
        return <Badge variant="secondary">{t.pending}</Badge>;
      case "cancelled":
        return <Badge variant="destructive">{t.cancelled}</Badge>;
      case "past_due":
        return <Badge variant="destructive">{t.pastDue}</Badge>;
      default:
        return <Badge variant="outline">{t.noSubscription}</Badge>;
    }
  };

  const formatCardBrand = (brand: string | null) => {
    if (!brand) return "";
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t.paymentMethod}
          </CardTitle>
          {statusBadge()}
        </div>
        {subscription.cancelAtPeriodEnd && subscription.currentPeriodEnd && (
          <CardDescription className="text-destructive">
            {t.cancelsOn} {formatDate(new Date(subscription.currentPeriodEnd))}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {hasPaymentMethod ? (
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-16 items-center justify-center rounded border bg-muted">
              {subscription.cardBrand ? (
                <span className="text-xs font-medium">
                  {formatCardBrand(subscription.cardBrand)}
                </span>
              ) : (
                <CreditCard className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div>
              {subscription.cardLast4 && (
                <p className="font-medium">•••• •••• •••• {subscription.cardLast4}</p>
              )}
              {subscription.cardExpiry && (
                <p className="text-sm text-muted-foreground">
                  {t.expires} {subscription.cardExpiry}
                </p>
              )}
              {!subscription.cardLast4 && subscription.paymentMethod && (
                <p className="font-medium capitalize">{subscription.paymentMethod}</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t.noPaymentMethod}</p>
        )}
      </CardContent>
    </Card>
  );
}
